import React, { useCallback, useEffect, useState } from 'react';
import { getLscMemberProviderSync, updateLscMember, LscApiError } from '../api';
import type {
  LscMemberProviderSyncResult,
  LscProviderSyncMatchBasis,
  LscProviderSyncRow,
} from '../types';
import ErrorBanner from './ErrorBanner';

interface NeonSportzMemberSyncPanelProps {
  leagueId: string;
  disabled?: boolean;
  onApplied?: () => void;
}

export function matchBasisLabel(basis?: LscProviderSyncMatchBasis | null): string {
  if (basis === 'discord_id') return 'Matched by Discord ID';
  if (basis === 'in_game_name') return 'Matched by in-game name';
  return '';
}

export function formatSyncedAt(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function memberName(row: LscProviderSyncRow): string {
  return row.member?.display_name || 'Member';
}

const NeonSportzMemberSyncPanel: React.FC<NeonSportzMemberSyncPanelProps> = ({
  leagueId,
  disabled = false,
  onApplied,
}) => {
  const [data, setData] = useState<LscMemberProviderSyncResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyingTeam, setApplyingTeam] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getLscMemberProviderSync(leagueId));
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load the NeonSportz roster.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyRow = async (row: LscProviderSyncRow) => {
    if (!row.member?.member_id) return;
    setApplyingTeam(row.team_label);
    setError(null);
    try {
      await updateLscMember(leagueId, row.member.member_id, {
        current_side_label: row.team_label,
        team_assignment_source_team_id: row.team_id || null,
      });
      await load();
      onApplied?.();
    } catch (err) {
      if (err instanceof LscApiError && err.code === 'team_already_claimed') {
        setError(`${row.team_label} is already assigned to another member.`);
      } else {
        setError(err instanceof LscApiError ? err.message : 'Could not assign the team.');
      }
    } finally {
      setApplyingTeam(null);
    }
  };

  const renderAction = (row: LscProviderSyncRow) => {
    switch (row.state) {
      case 'confirmed': {
        const twitchConnected =
          row.member?.status === 'active' &&
          Boolean(row.member.twitch_login || row.member.twitch_user_id);
        return (
          <span
            className={twitchConnected ? 'text-emerald-400' : 'text-amber-200'}
            data-testid="provider-sync-confirmed"
          >
            {twitchConnected ? 'Connected' : 'Team assigned · no member connected'}
            {row.provider_suggests ? (
              <span className="block text-xs text-secondary-400">
                NeonSportz points to {row.provider_suggests.display_name}. Change it from Edit member
                if that is right.
              </span>
            ) : null}
          </span>
        );
      }
      case 'suggested':
        return (
          <button
            type="button"
            className="btn-secondary"
            disabled={disabled || applyingTeam !== null}
            onClick={() => void applyRow(row)}
            data-testid={`provider-sync-apply-${row.team_label}`}
          >
            {applyingTeam === row.team_label ? 'Assigning...' : `Assign ${row.team_label}`}
          </button>
        );
      case 'member_has_other_team':
        return (
          <span className="text-xs text-amber-300">
            {memberName(row)} is already on {row.member?.current_side_label}. Change it from Edit
            member if that is right.
          </span>
        );
      case 'ambiguous':
        return (
          <span className="text-xs text-amber-300">
            More than one member matches ({(row.candidates || []).map((c) => c.display_name).join(', ')}).
            Assign from Edit member.
          </span>
        );
      default:
        return <span className="text-xs text-secondary-400">{row.message}</span>;
    }
  };

  const rows = data?.rows || [];
  const syncedAt = formatSyncedAt(data?.synced_at);

  return (
    <div className="card space-y-4" data-testid="neonsportz-member-sync-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white mb-1">NeonSportz teams</h2>
          {data?.state === 'ready' ? (
            <p className="text-sm text-secondary-300" data-testid="provider-sync-status">
              {data.synced_label}
              {syncedAt ? ` · ${syncedAt}` : ''}
              {data.scope_label ? ` · ${data.scope_label}` : ''}
            </p>
          ) : null}
          <p className="text-sm text-secondary-400">
            Live detection needs each member on their team. Review each suggestion and assign it —
            nothing is assigned automatically.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          disabled={loading}
          onClick={() => void load()}
          data-testid="provider-sync-refresh"
        >
          {loading ? 'Syncing...' : 'Re-sync'}
        </button>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {data && data.state !== 'ready' ? (
        <p className="text-sm text-amber-300" data-testid="provider-sync-unavailable">
          {data.message}
        </p>
      ) : null}

      {data?.state === 'ready' ? (
        <p className="text-sm text-secondary-300" data-testid="provider-sync-counts">
          {data.unassigned_member_count} member{data.unassigned_member_count === 1 ? '' : 's'} without a
          team · {data.provider_teams_without_member_count} NeonSportz team
          {data.provider_teams_without_member_count === 1 ? '' : 's'} with no LSC member
        </p>
      ) : null}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left text-secondary-300">
            <thead>
              <tr className="text-secondary-400 border-b border-secondary-700">
                <th className="py-2 pr-4">Team</th>
                <th className="py-2 pr-4">NeonSportz owner</th>
                <th className="py-2 pr-4">LSC member</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.team_id || row.team_label}
                  className="border-b border-secondary-800"
                  data-testid={`provider-sync-row-${row.team_label}`}
                >
                  <td className="py-2 pr-4">{row.team_label}</td>
                  <td className="py-2 pr-4">{row.provider_owner_name || '—'}</td>
                  <td className="py-2 pr-4">
                    {row.member ? (
                      <>
                        {row.member.display_name}
                        {row.match_basis ? (
                          <span className="block text-xs text-secondary-400">
                            {matchBasisLabel(row.match_basis)}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2">{renderAction(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NeonSportzMemberSyncPanel;
